import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image,Font } from '@react-pdf/renderer';
Font.register({ family: 'Roboto', src: 'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&family=Roboto:wght@300&display=swap' });
// Interface for item type
interface InvoiceItem {
  item_id: number | string;
  description?: string;
  quantity: number;
  rate: number;
  discount_percent?: number;
  discount_amount?: number;
  total_amount?: number;
  unit_id?: number;
  tax_percent?: number;
  tax_amount?: number;
  taxable_amount?: number;
  gst_breakdown?: Array<{
    code: string;
    component_code: string;
    component_id: number;
    rate: number;
    amount: number;
  }>;
  item?: {
    id: number;
    name: string;
    code?: string;
    description?: string;
  };
}

// Interface for customer/party type
interface Party {
  name: string;
  party_id?: number;
  billing_address?: {
    address_line_1: string;
    address_line_2?: string;
    city: string;
    state: string;
    pincode: string;
    country?: string;
  };
  contact_person?: string;
  phone?: string;
  email?: string;
  gstin?: string;
}

// Interface for sales invoice type
interface SalesInvoice {
  id?: number;
  voucher_number: string;
  transaction_date: string;
  due_date?: string;
  reference_number?: string;
  narration?: string;
  party_id?: number;
  party?: Party;
  site_id?: number;
  site?: {
    name: string;
    address?: string;
  };
  items: InvoiceItem[];
  total_amount?: number;
  voucher_type?: {
    prefix?: string;
  };
  additional_fields?: {
    total_amount?: string;
    original_total?: string;
  };
}

// Styles for the PDF document
const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 10,
    paddingTop: 35,
    paddingBottom: 65,
    paddingHorizontal: 35,
    backgroundColor: '#ffffff',
    lineHeight: 1.4,
  },
  headerSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#eaeaea',
  },
  companyInfo: {
    flexDirection: 'column',
    textAlign: 'left',
    maxWidth: '50%',
  },
  logo: {
    width: 80,
    height: 'auto',
    marginBottom: 5,
  },
  companyName: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 3,
    color: '#333333',
  },
  companyAddress: {
    fontSize: 9,
    color: '#555555',
  },
  documentInfo: {
    flexDirection: 'column',
    textAlign: 'right',
    maxWidth: '40%',
  },
  documentTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 5,
    color: '#1a73e8',
  },
  documentDetail: {
    fontSize: 9,
    marginBottom: 2,
    color: '#444444',
  },
  customerSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 25,
  },
  billTo: {
    maxWidth: '45%',
  },
  shipTo: {
    maxWidth: '45%',
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 5,
    color: '#555555',
    textTransform: 'uppercase',
  },
  customerName: {
    fontWeight: 'bold',
    marginBottom: 2,
    color: '#333333',
  },
  customerAddress: {
    fontSize: 9,
    color: '#555555',
  },
  table: {
    // Using 'as any' to get around the type error with 'table' 
    display: 'table' as any,
    width: 'auto',
    borderStyle: 'solid',
    borderColor: '#e0e0e0',
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    marginBottom: 20,
  },
  tableRow: {
    flexDirection: 'row',
    backgroundColor: '#ffffff',
  },
  tableHeaderRow: {
    flexDirection: 'row',
    backgroundColor: '#f2f2f2',
    borderBottomColor: '#bdbdbd',
    borderBottomWidth: 1,
  },
  tableColHeader: {
    borderStyle: 'solid',
    borderColor: '#e0e0e0',
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 5,
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: 9,
    color: '#444444',
  },
  tableCol: {
    borderStyle: 'solid',
    borderColor: '#e0e0e0',
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 5,
    fontSize: 9,
  },
  colNo: { width: '5%', textAlign: 'center' },
  colItem: { width: '25%', textAlign: 'left' },
  colQty: { width: '8%', textAlign: 'right' },
  colRate: { width: '10%', textAlign: 'right' },
  colDiscPercent: { width: '8%', textAlign: 'right' },
  colDiscAmount: { width: '10%', textAlign: 'right' },
  colTaxableAmount: { width: '12%', textAlign: 'right' },
  colTaxAmount: { width: '10%', textAlign: 'right' },
  colAmount: { width: '12%', textAlign: 'right' },

  totalsSection: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 0,
    marginBottom: 20,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: '#eaeaea',
  },
  totalsContainer: {
    width: '40%',
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 3,
  },
  totalLabel: {
    fontSize: 10,
    color: '#555555',
    textAlign: 'left',
  },
  totalValue: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#333333',
    textAlign: 'right',
  },
  grandTotalLabel: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#333333',
    textAlign: 'left',
    marginTop: 5,
  },
  grandTotalValue: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#333333',
    textAlign: 'right',
    marginTop: 5,
  },
  notesSection: {
    marginTop: 10,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: '#eaeaea',
    fontSize: 9,
    color: '#555555',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 35,
    right: 35,
    textAlign: 'center',
    fontSize: 8,
    color: 'grey',
    borderTopWidth: 1,
    borderTopColor: '#eaeaea',
    paddingTop: 5,
  },
  pageNumber: {
    position: 'absolute',
    fontSize: 8,
    bottom: 15,
    left: 0,
    right: 35,
    textAlign: 'right',
    color: 'grey',
  },
  signatureSection: {
    marginTop: 30,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  signatureBox: {
    width: '40%',
  },
  signatureLine: {
    borderTopWidth: 1,
    borderTopColor: '#555555',
    marginTop: 50,
    marginBottom: 5,
  },
  signatureText: {
    fontSize: 9,
    textAlign: 'center',
  },
});

// Helper functions
const formatCurrency = (amount: number | string | undefined | null): string => {
  const num = Number(amount || 0);
  if (isNaN(num)) return '0';
  // Return whole numbers without decimals
  return Number.isInteger(num) ? num.toString() : num.toFixed(2);
};

const formatDate = (date: Date | string | undefined | null): string => {
  if (!date) return '-';
  try {
    return new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch (e) {
    return '-';
  }
};

// Define the interface for the props
interface SalesInvoicePDFTemplateProps {
  salesInvoice: SalesInvoice;
  partyDetails?: Party;
  companyDetails: {
    name: string;
    address: string;
    cityStatePin: string;
    phone?: string;
    email?: string;
    website?: string;
    logoUrl?: string;
    gstin?: string;
  };
}

// The PDF Template component
export const SalesInvoicePDFTemplate: React.FC<SalesInvoicePDFTemplateProps> = ({
  salesInvoice,
  partyDetails,
  companyDetails,
}) => {
  const customer = partyDetails || salesInvoice.party;

  // Use backend-calculated totals
  const calculateItemTotal = (item: InvoiceItem): number => {
    return Number(item.total_amount || 0);
  };

  const items = (salesInvoice.items || []).map(item => ({
    ...item,
    total: calculateItemTotal(item)
  }));

  // Calculate totals from items (don't use the rounded additional_fields.total_amount)
  let totalTaxableAmount = 0;
  let totalTax = 0;
  let grandTotal = 0;
  let totalDiscount = 0;
  
  // Group tax by GST rates for proper display
  const taxBreakdown: { [key: number]: { amount: number, taxableAmount: number } } = {};
  
  items.forEach((item) => {
    const taxableAmount = Number(item.taxable_amount || 0);
    const taxAmount = Number(item.tax_amount || 0);
    const totalAmount = Number(item.total_amount || 0);
    const discountAmount = Number(item.discount_amount || 0);
    
    totalTaxableAmount += taxableAmount;
    totalTax += taxAmount;
    grandTotal += totalAmount;
    totalDiscount += discountAmount;
    
    // Group tax by rate using GST breakdown if available
    if (item.gst_breakdown && item.gst_breakdown.length > 0) {
      const totalRate = item.gst_breakdown.reduce((sum, gst) => sum + (gst.rate || 0), 0);
      if (totalRate > 0) {
        const roundedRate = Math.round(totalRate * 2) / 2; // Round to nearest 0.5
        if (!taxBreakdown[roundedRate]) {
          taxBreakdown[roundedRate] = { amount: 0, taxableAmount: 0 };
        }
        taxBreakdown[roundedRate].amount += taxAmount;
        taxBreakdown[roundedRate].taxableAmount += taxableAmount;
      }
    } else if (taxAmount > 0 && taxableAmount > 0) {
      // Estimate rate from tax amount if no GST breakdown
      const estimatedRate = Math.round((taxAmount / taxableAmount) * 100 * 2) / 2;
      if (!taxBreakdown[estimatedRate]) {
        taxBreakdown[estimatedRate] = { amount: 0, taxableAmount: 0 };
      }
      taxBreakdown[estimatedRate].amount += taxAmount;
      taxBreakdown[estimatedRate].taxableAmount += taxableAmount;
    }
  });

  return (
    <Document title={`Invoice ${salesInvoice.voucher_number}`}>
      <Page size="A4" style={styles.page}>
        {/* Header Section */}
        <View style={styles.headerSection}>
          <View style={styles.companyInfo}>
            {companyDetails.logoUrl && <Image style={styles.logo} src={companyDetails.logoUrl} />}
            <Text style={styles.companyName}>{companyDetails.name}</Text>
            <Text style={styles.companyAddress}>{companyDetails.address}</Text>
            <Text style={styles.companyAddress}>{companyDetails.cityStatePin}</Text>
            {companyDetails.phone && <Text style={styles.companyAddress}>Phone: {companyDetails.phone}</Text>}
            {companyDetails.email && <Text style={styles.companyAddress}>Email: {companyDetails.email.replace('mikro.in', 'mikroplatforms.com')}</Text>}
            {companyDetails.website && <Text style={styles.companyAddress}>Website: {companyDetails.website.replace('mikro.in', 'mikroplatforms.com')}</Text>}
            {companyDetails.gstin && <Text style={styles.companyAddress}>GSTIN: {companyDetails.gstin}</Text>}
          </View>
          <View style={styles.documentInfo}>
            <Text style={styles.documentTitle}>INVOICE</Text>
            <Text style={styles.documentDetail}>Invoice #: {salesInvoice.voucher_number}</Text>
            <Text style={styles.documentDetail}>Date: {formatDate(salesInvoice.transaction_date)}</Text>
            {salesInvoice.due_date && (
              <Text style={styles.documentDetail}>Due Date: {formatDate(salesInvoice.due_date)}</Text>
            )}
            {salesInvoice.reference_number && (
              <Text style={styles.documentDetail}>Reference: {salesInvoice.reference_number}</Text>
            )}
          </View>
        </View>

        {/* Customer Section */}
        <View style={styles.customerSection}>
          <View style={styles.billTo}>
            <Text style={styles.sectionTitle}>Bill To</Text>
            <Text style={styles.customerName}>{customer?.name || 'N/A'}{customer?.gstin ? ` (GSTIN: ${customer.gstin})` : ''}</Text>
            {customer?.billing_address && (
              <>
                <Text style={styles.customerAddress}>{customer.billing_address.address_line_1}</Text>
                {customer.billing_address.address_line_2 && (
                  <Text style={styles.customerAddress}>{customer.billing_address.address_line_2}</Text>
                )}
                <Text style={styles.customerAddress}>
                  {customer.billing_address.city}, {customer.billing_address.state} {customer.billing_address.pincode}
                </Text>
                {customer.billing_address.country && (
                  <Text style={styles.customerAddress}>{customer.billing_address.country}</Text>
                )}
              </>
            )}
            {customer?.contact_person && <Text style={styles.customerAddress}>Attn: {customer.contact_person}</Text>}
            {customer?.phone && <Text style={styles.customerAddress}>Phone: {customer.phone}</Text>}
            {customer?.email && <Text style={styles.customerAddress}>Email: {customer.email}</Text>}
          </View>
          <View style={styles.shipTo}>
            <Text style={styles.sectionTitle}>Ship To</Text>
            {salesInvoice.site ? (
              <>
                <Text style={styles.customerName}>{salesInvoice.site.name || 'N/A'}</Text>
                <Text style={styles.customerAddress}>{salesInvoice.site.address || 'N/A'}</Text>
              </>
            ) : (
              <Text style={styles.customerAddress}>Same as billing address</Text>
            )}
          </View>
        </View>

        {/* Items Table */}
        <View style={styles.table}>
          {/* Table Header */}
          <View style={styles.tableHeaderRow} fixed>
            <Text style={[styles.tableColHeader, styles.colNo]}>No.</Text>
            <Text style={[styles.tableColHeader, styles.colItem]}>Product</Text>
            <Text style={[styles.tableColHeader, styles.colQty]}>Qty</Text>
            <Text style={[styles.tableColHeader, styles.colRate]}>Rate</Text>
            <Text style={[styles.tableColHeader, styles.colDiscPercent]}>Disc %</Text>
            <Text style={[styles.tableColHeader, styles.colDiscAmount]}>Disc Amt</Text>
            <Text style={[styles.tableColHeader, styles.colTaxableAmount]}>Taxable Amt</Text>
            <Text style={[styles.tableColHeader, styles.colTaxAmount]}>Tax Amt</Text>
            <Text style={[styles.tableColHeader, styles.colAmount]}>Amount</Text>
          </View>
          {/* Table Body */}
          {items.map((item, index) => {
            const quantity = Number(item.quantity || 0);
            const rate = Number(item.rate || 0);
            const discountPercent = Number(item.discount_percent || 0);
            const discountAmount = Number(item.discount_amount || 0);
            const taxableAmount = Number(item.taxable_amount || 0);
            const taxAmount = Number(item.tax_amount || 0);
            const totalAmount = Number(item.total_amount || 0);
            
            return (
              <View key={index} style={styles.tableRow} wrap={false}>
                <Text style={[styles.tableCol, styles.colNo]}>
                  {index + 1}
                </Text>
                <Text style={[styles.tableCol, styles.colItem]}>
                  {item.item?.name || item.description || `Item ID: ${item.item_id}`}
                </Text>
                <Text style={[styles.tableCol, styles.colQty]}>
                  {quantity.toString()}
                </Text>
                <Text style={[styles.tableCol, styles.colRate]}>
                  {rate.toString()}
                </Text>
                <Text style={[styles.tableCol, styles.colDiscPercent]}>
                  {discountPercent.toString()}
                </Text>
                <Text style={[styles.tableCol, styles.colDiscAmount]}>
                  {discountAmount.toString()}
                </Text>
                <Text style={[styles.tableCol, styles.colTaxableAmount]}>
                  {taxableAmount.toFixed(2)}
                </Text>
                <Text style={[styles.tableCol, styles.colTaxAmount]}>
                  {taxAmount.toFixed(2)}
                </Text>
                <Text style={[styles.tableCol, styles.colAmount]}>
                  {totalAmount.toFixed(2)}
                </Text>
              </View>
            );
          })}
        </View>

        {/* Totals Section */}
        <View style={styles.totalsSection}>
          <View style={styles.totalsContainer}>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Subtotal:</Text>
              <Text style={styles.totalValue}>₹{formatCurrency(totalTaxableAmount)}</Text>
            </View>
            
            {/* Tax Breakdown by Rate */}
            {Object.entries(taxBreakdown).sort((a, b) => Number(a[0]) - Number(b[0])).map(([rate, { amount }]) => {
              const rateNum = Number(rate);
              if (rateNum <= 0) return null;
              
              // For GST, split into CGST and SGST
              const cgstRate = rateNum / 2;
              const sgstRate = rateNum / 2;
              const cgstAmount = amount / 2;
              const sgstAmount = amount / 2;
              
              return (
                <React.Fragment key={rate}>
                  <View style={styles.totalRow}>
                    <Text style={styles.totalLabel}>CGST @ {cgstRate}%:</Text>
                    <Text style={styles.totalValue}>₹{formatCurrency(cgstAmount)}</Text>
                  </View>
                  <View style={styles.totalRow}>
                    <Text style={styles.totalLabel}>SGST @ {sgstRate}%:</Text>
                    <Text style={styles.totalValue}>₹{formatCurrency(sgstAmount)}</Text>
                  </View>
                </React.Fragment>
              );
            })}
            
            <View style={styles.totalRow}>
              <Text style={styles.grandTotalLabel}>Total:</Text>
              <Text style={styles.grandTotalValue}>₹{formatCurrency(grandTotal)}</Text>
            </View>
          </View>
        </View>

        {/* Notes Section */}
        {salesInvoice.narration && (
          <View style={styles.notesSection}>
            <Text style={styles.sectionTitle}>Notes</Text>
            <Text>{salesInvoice.narration}</Text>
          </View>
        )}
        
        {/* Signature Section */}
        <View style={styles.signatureSection}>
          <View style={styles.signatureBox}>
            <View style={styles.signatureLine}></View>
            <Text style={styles.signatureText}>Customer Signature</Text>
          </View>
          
          <View style={styles.signatureBox}>
            <View style={styles.signatureLine}></View>
            <Text style={styles.signatureText}>For {companyDetails.name}</Text>
          </View>
        </View>

        {/* Footer */}
        <Text style={styles.footer} fixed>
          Thank you for your business! | Terms & Conditions Apply | Generated On: {formatDate(new Date())}
        </Text>
        
        {/* Page Number */}
        <Text 
          style={styles.pageNumber} 
          render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} 
          fixed 
        />
      </Page>
    </Document>
  );
}; 